import { AbstractPlayer } from './abstract-player'

const EXTRACT_TITLE_TIMEOUT = 500

/**
 * SendToNews video player.
 */
export class SendToNews extends AbstractPlayer {
    private observer: MutationObserver | undefined = undefined

    public constructor(protected element: HTMLElement) {
        super(element)
        this.iframeData = JSON.parse(this.element.dataset.evtVal || '{}')
        this.setupTitleTracking()
    }

    /**
     * Sets up title tracking via Shadow DOM and MutationObserver
     */
    private setupTitleTracking(): void {
        // Set up MutationObserver to watch for stn-player component and shadow DOM
        this.observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement) {
                        // Check if this is the stn-player component
                        if (node.tagName === 'STN-PLAYER') {
                            this.tryExtractTitleFromShadowDOM(node)
                        }

                        // Also check children for stn-player
                        const stnPlayer = node.querySelector('stn-player')
                        if (stnPlayer) {
                            this.tryExtractTitleFromShadowDOM(
                                stnPlayer as HTMLElement
                            )
                        }
                    }
                })
            }
        })

        // Observe the S2N player container
        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        })

        // Also check if stn-player already exists
        const existingStnPlayer = document.querySelector('stn-player')
        if (existingStnPlayer) {
            this.tryExtractTitleFromShadowDOM(existingStnPlayer as HTMLElement)
        }
    }

    /**
     * Attempts to extract title from stn-player shadow DOM
     */
    private tryExtractTitleFromShadowDOM(stnPlayer: HTMLElement): void {
        let shadowRoot = stnPlayer.shadowRoot

        // First check if stn-player itself has a shadow root
        // If not, look for voltax-player child which has the shadow root
        if (!shadowRoot) {
            const voltaxPlayer = stnPlayer.querySelector('voltax-player')
            if (voltaxPlayer) {
                shadowRoot = voltaxPlayer.shadowRoot
            }
        }

        if (!shadowRoot) {
            // Shadow root might not be attached yet, try again after a delay
            setTimeout(() => {
                this.tryExtractTitleFromShadowDOM(stnPlayer)
            }, EXTRACT_TITLE_TIMEOUT)
            return
        }

        this.extractTitleFromRoot(shadowRoot)
    }

    /**
     * Extracts title from shadow root and sets up observer
     */
    private extractTitleFromRoot(shadowRoot: ShadowRoot): void {
        // Find the title element
        const titleElement = shadowRoot.querySelector('.title')
        if (titleElement && titleElement.textContent) {
            const title = titleElement.textContent.trim()
            this.updateTitle(title)
        }
        // Set up an observer to watch for changes to the .title element
        // Observe the .title element directly, or fallback to shadow root if not found yet
        const observeTarget = titleElement || shadowRoot
        const shadowObserver = new MutationObserver(() => {
            const title = shadowRoot.querySelector('.title')
            if (title && title.textContent) {
                const titleText = title.textContent.trim()
                // Only update if title actually changed
                if (titleText !== this.videoTitle) {
                    this.updateTitle(titleText)
                }
            }
        })

        shadowObserver.observe(observeTarget, {
            childList: true, // Watch for when inner span content changes
            subtree: true, // Need subtree to catch text changes in nested spans
            characterData: true,
            attributes: false // Ignore attribute changes (classes, styles, etc)
        })
    }

    /**
     * Updates the video title and notifies the sticky video component
     */
    private updateTitle(title: string): void {
        if (title && title !== this.videoTitle) {
            this.videoTitle = title
            this.changeStickyTitle(title)
        }
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */
    setMute(_: boolean) {
        return
    }
    setControls(_: boolean) {
        return
    }
    setLoop(_: boolean) {
        return
    }
    setAutoplay(_: boolean) {
        return
    }
    async init() {
        return
    }
    get title() {
        return this.videoTitle
    }
    get index() {
        return 0
    }
    pause() {
        return
    }
    play() {
        return
    }
    playVideoAt(_: number) {
        return
    }
    destroy() {
        // Clean up observers
        if (this.observer) {
            this.observer.disconnect()
            this.observer = undefined
        }
        return
    }
    cuePlaylist(_: YT.IPlaylistSettings) {
        return
    }
    getState() {
        return ''
    }
    isPausable() {
        return true
    }
    onError(_: YT.OnErrorEvent | jwplayer.ErrorParam) {
        return
    }
    render() {
        // create a script element
        const script = document.createElement('script')
        const cid = this.element.dataset.stnCid
        const fk = this.element.dataset.stnFk
        const stnFloat = this.element.dataset.stnFloat
        const scriptSrc = `//embed.sendtonews.com/player2/embedcode.php?fk=${fk}&cid=${cid}${stnFloat}`

        script.setAttribute('type', 'text/javascript')
        script.setAttribute('async', '')
        script.setAttribute('data-type', 'text/javascript')
        script.setAttribute('src', scriptSrc)

        if (this.element) {
            this.element.insertAdjacentElement('afterend', script)
        }
        return
    }
}
